import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import type { SetupStep } from '../useSetupStatus';

/** The permission key itself, set apart from the sentence that names it. */
function Key({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-subtle px-1 py-0.5 font-mono text-xs text-ink">{children}</code>
  );
}

function Notice({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <span className="mt-0.5 shrink-0 text-ink-muted">{icon}</span>
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium">{title}</p>
          <div className="space-y-1 text-sm text-ink-muted">{children}</div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * A step this actor cannot read.
 *
 * `restricted` is not `missing`, and the difference is the whole notice: the
 * step may well be finished already, and nobody can tell from here. A venue is
 * normally set up by more than one pair of hands — an owner makes the branch, a
 * manager the floors, an RBAC admin the staff — so this must be a place to walk
 * past, never a dead end. The keys are named because "ask an administrator" is
 * not a request anybody can act on: whoever is asked has to know which key to
 * grant.
 */
export function RestrictedNotice({ step }: { step: SetupStep }) {
  const { t } = useTranslation();
  return (
    <Notice icon={<Icon as={EyeOff} aria-hidden />} title={t('setup.wizard.restricted.title')}>
      <p>{t('setup.checklist.restrictedHint')}</p>
      <p>
        {t('setup.wizard.restricted.needsView')} <Key>{step.needs.view}</Key>
      </p>
      <p>
        {t('setup.wizard.restricted.needsFix')} <Key>{step.needs.fix}</Key>
      </p>
      <p>{t('setup.wizard.restricted.carryOn')}</p>
    </Notice>
  );
}

/**
 * The probe answered with something that was not a 403.
 *
 * Shown as "unknown", not "empty": a network failure that rendered as an empty
 * list would send someone to re-create things that are already there.
 */
export function ErrorNotice({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Notice icon={<Icon as={CircleAlert} aria-hidden />} title={t('setup.wizard.error.title')}>
      <p>{t('setup.checklist.errorHint')}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('common.tryAgain')}
      </Button>
    </Notice>
  );
}

/**
 * This actor can see the step but cannot change it.
 *
 * The panel below still renders — reading it is genuinely useful, and it is how
 * someone confirms the step is done before moving on — but every Add and Edit in
 * it is withheld by its own permission check, and a row of missing buttons
 * explains nothing on its own.
 */
export function ReadOnlyNotice({ step }: { step: SetupStep }) {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-1.5 text-sm text-ink-muted">
      <Icon as={KeyRound} size="sm" aria-hidden />
      {t('setup.wizard.readOnly')} <Key>{step.needs.fix}</Key>
    </p>
  );
}
